import { useAppShell } from "@salestrak/ui";
import { useCallback } from "react";

import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { SETTINGS_BROWSE_ID, useModuleRail } from "./module-rail";

/**
 * The two doors between the app's navigation and settings' navigation — every
 * "Settings" entry (account menu, workspace menu) and every "Back to app"
 * (the settings pane's arrow) goes through here
 * so the crossing means the same thing on every tier.
 *
 * On COMPACT the drawer IS the navigation, so a door swaps the drawer's pane
 * in place instead of navigating: entering settings shows the settings pane
 * over whatever page is up, leaving it shows the module strip and Home's
 * menu over the settings page. The user is choosing where to go; the row
 * they pick is the commit (and closes the drawer). Navigating at the door
 * would land them on a default page they never asked for, with the drawer
 * closed out from under them. Dismissing the drawer instead abandons the
 * browse — on the next open the pane follows the route again.
 *
 * On the other tiers a door navigates: the rail and pinned pane are on
 * screen, so the route is what says which world you are in.
 */
export function useSettingsDoors(): {
  /** Enter settings. `navigate` is the caller's own navigation (which
   * settings page, push vs replace) — run only where the door navigates. */
  enterSettings: (navigate: () => void) => void;
  /** Leave settings for the app: the workspace root elsewhere, Home's menu
   * in the drawer on compact. */
  leaveSettings: () => void;
} {
  const { tier, setOpenMobile } = useAppShell();
  const { setBrowseTileId } = useModuleRail();
  const { replace } = useAppNavigate();

  const enterSettings = useCallback(
    (navigate: () => void) => {
      if (tier === "compact") {
        setBrowseTileId(SETTINGS_BROWSE_ID);
        setOpenMobile(true);
        return;
      }
      navigate();
    },
    [tier, setBrowseTileId, setOpenMobile],
  );

  const leaveSettings = useCallback(() => {
    if (tier === "compact") {
      setBrowseTileId("home");
      setOpenMobile(true);
      return;
    }
    replace("/");
  }, [tier, setBrowseTileId, setOpenMobile, replace]);

  return { enterSettings, leaveSettings };
}
